/**
 * Cross-module X XWP-74 (XO-14, XD-224; CP §11, §15.20) — the « nouvelle fenêtre » flow, pure part.
 *
 * Source window (a form with work in progress):
 *   - `assurerJetonSource(win)` gives the window a stable name `pharmakon-fenetre-<jeton>` (kept
 *     across in-tab navigations and reloads — `window.name` belongs to the tab);
 *   - `ouvrirFenetre({ win, chemin })` opens the supporting page in a NEW tab / window with
 *     `?source=<jeton>`, keeping the opener (XD-224: this flow only; every other new tab keeps
 *     `noopener`, refonte A-35).
 * Opened window:
 *   - `capturerContexte({ search, storage })` reads the jeton from its first URL and keeps it in its
 *     own sessionStorage (in-tab navigation drops the query string); a fresh child also drops the
 *     sessionStorage copy the browser made from its opener (the caisse windows mirror, hand-off keys…),
 *     so it starts like any new tab;
 *   - `etatSource` / `retournerVersSource` focus the opener when it is still open AND still carries the
 *     jeton's name, otherwise report why (one clear sentence, XD-224). Nothing is ever navigated to: a
 *     jeton can only designate a window of this browsing session.
 *
 * DOM objects (window, storage) are injected, so every rule is unit-tested with plain objects.
 */
import { FENETRE_SOURCE, PAGES_SUPPORT, PAGES_SUPPORT_EXCLUES } from "../config/fenetre-source.config.js";
import { ROUTE_ACCESS, canAccessRoute } from "../config/routeAccess.config.js";
import { STOCK_ROUTE, stockProduitPath } from "../config/stock-views.config.js";
import { patternMatches, patternScore, routeOf } from "./nav-tree.js";

const JETON_RE = new RegExp(`^[a-z0-9]{${FENETRE_SOURCE.JETON_LONGUEUR}}$`);
const ORIGINE_FICTIVE = "http://pharmakon.invalid";

/** A fresh jeton (lowercase base-36, FENETRE_SOURCE.JETON_LONGUEUR characters). */
export function nouveauJeton(aleatoire = Math.random) {
  let out = "";
  while (out.length < FENETRE_SOURCE.JETON_LONGUEUR) out += Math.floor(aleatoire() * 36).toString(36);
  return out.slice(0, FENETRE_SOURCE.JETON_LONGUEUR);
}

/** True for a well-formed jeton. */
export function jetonValide(jeton) {
  return typeof jeton === "string" && JETON_RE.test(jeton);
}

/** The `window.name` a source window carries for `jeton`. */
export function nomDeFenetre(jeton) {
  return `${FENETRE_SOURCE.PREFIXE_NOM}${jeton}`;
}

/** The jeton of a source window's name, or null when the name is not one of ours. */
export function jetonDuNom(nom) {
  if (typeof nom !== "string" || !nom.startsWith(FENETRE_SOURCE.PREFIXE_NOM)) return null;
  const j = nom.slice(FENETRE_SOURCE.PREFIXE_NOM.length);
  return jetonValide(j) ? j : null;
}

/**
 * The source window's jeton: its current name when it already is one of ours (a window opened twice
 * as a source keeps one identity), else a fresh one written into `win.name`.
 */
export function assurerJetonSource(win, aleatoire = Math.random) {
  const existant = jetonDuNom(win?.name);
  if (existant) return existant;
  const jeton = nouveauJeton(aleatoire);
  if (win) win.name = nomDeFenetre(jeton);
  return jeton;
}

/** `chemin` (an in-app path, query and hash kept) with `?source=<jeton>` set. */
export function urlAvecSource(chemin, jeton) {
  const url = new URL(String(chemin ?? "/"), ORIGINE_FICTIVE);
  url.searchParams.set(FENETRE_SOURCE.PARAM, jeton);
  return `${url.pathname}${url.search}${url.hash}`;
}

/** The jeton of `?source=` in a query string, or null (absent or malformed). */
export function lireJetonUrl(search) {
  let j = null;
  try {
    j = new URLSearchParams(String(search ?? "")).get(FENETRE_SOURCE.PARAM);
  } catch {
    return null;
  }
  return jetonValide(j) ? j : null;
}

/** The pathname of an in-app path (`/stock?x=1#h` → `/stock`). */
export function cheminSansRequete(chemin) {
  try {
    return new URL(String(chemin ?? "/"), ORIGINE_FICTIVE).pathname;
  } catch {
    return routeOf(chemin);
  }
}

/** The ROUTE_ACCESS pattern a concrete pathname resolves to (the most specific one), or null. */
export function motifDeRoute(chemin) {
  const p = cheminSansRequete(chemin);
  let best = null;
  for (const motif of Object.keys(ROUTE_ACCESS)) {
    if (!patternMatches(motif, p)) continue;
    if (!best || patternScore(motif) > patternScore(best)) best = motif;
  }
  return best;
}

/** Whether `role` may open the page `chemin` (routeAccess, fail closed on an unknown page). */
export function peutOuvrir(role, chemin) {
  const motif = motifDeRoute(chemin);
  return Boolean(motif) && canAccessRoute(role, motif);
}

/**
 * The supporting page (CP §15.20) `chemin` points to, or null: creation routes stay steps of the
 * form, every other page is left to the screen's own leave dialog.
 */
export function pageSupport(chemin) {
  const p = cheminSansRequete(chemin);
  if (PAGES_SUPPORT_EXCLUES.some((x) => patternMatches(x, p))) return null;
  return PAGES_SUPPORT.find((page) => patternMatches(page.motif, p)) ?? null;
}

/** « Stock & lots » of a product (the `produit_id` filter, XD-139), else the whole stock. */
export function cheminStockLots(produitId) {
  return produitId != null && produitId !== "" ? stockProduitPath(produitId) : STOCK_ROUTE;
}

/**
 * Open `chemin` in a new window that keeps this one as its opener (XD-224).
 * @returns {{ ok: true, fenetre: any, jeton: string } | { ok: false, raison: "bloquee" }}
 */
export function ouvrirFenetre({ win, chemin, aleatoire = Math.random }) {
  const jeton = assurerJetonSource(win, aleatoire);
  let fenetre = null;
  try {
    fenetre = win.open(urlAvecSource(chemin, jeton), FENETRE_SOURCE.CIBLE);
  } catch {
    fenetre = null;
  }
  if (!fenetre) return { ok: false, raison: "bloquee" };
  return { ok: true, fenetre, jeton };
}

/**
 * The opened window's context on load. A jeton in the URL that differs from the stored one is a fresh
 * child: the sessionStorage the browser copied from the opener is dropped (it holds the opener's
 * per-tab state — caisse windows mirror, hand-off keys — that this window must not replay), then the
 * jeton is stored. Without a jeton in the URL, the stored one (a reload, an in-tab navigation) applies.
 * @returns {{ jeton: string | null, nouveau: boolean }}
 */
export function capturerContexte({ search, storage }) {
  const depuisUrl = lireJetonUrl(search);
  let stocke = null;
  try {
    stocke = storage?.getItem(FENETRE_SOURCE.CLE_SESSION) ?? null;
  } catch {
    stocke = null;
  }
  if (depuisUrl) {
    if (depuisUrl === stocke) return { jeton: depuisUrl, nouveau: false };
    try {
      storage?.clear();
      storage?.setItem(FENETRE_SOURCE.CLE_SESSION, depuisUrl);
    } catch {
      /* storage unavailable: the jeton still holds for this page */
    }
    return { jeton: depuisUrl, nouveau: true };
  }
  return { jeton: jetonValide(stocke) ? stocke : null, nouveau: false };
}

/**
 * Where the source window stands, seen from the opened one.
 * @returns {"ouverte" | "fermee" | "absente"} « absente »: no opener (a duplicated tab, a pasted URL)
 *   or an opener that is no longer this jeton's window (it was replaced by another site)
 */
export function etatSource({ win, jeton }) {
  if (!jetonValide(jeton)) return "absente";
  const opener = win?.opener ?? null;
  if (!opener) return "absente";
  try {
    if (opener.closed) return "fermee";
    return opener.name === nomDeFenetre(jeton) ? "ouverte" : "absente";
  } catch {
    // Cross-origin opener (the source window left the application): not our window any more.
    return "absente";
  }
}

/**
 * The page label of a source window from its document title (« Nouvel achat · Achats — Pharmakon »
 * → « Nouvel achat »; « FA-0012 — Facture d'achat — Pharmakon » → « FA-0012 — Facture d'achat »),
 * or null. The title is written by the shell from the navigation tree (nav-tree `windowTitleFor`).
 */
export function libelleDepuisTitre(titre, appTitle) {
  let t = String(titre ?? "").trim();
  if (!t) return null;
  const suffixe = ` — ${appTitle}`;
  if (appTitle && t.endsWith(suffixe)) t = t.slice(0, -suffixe.length);
  else if (appTitle && (t === appTitle || t.startsWith(`${appTitle} — `))) return null;
  const point = t.indexOf(" · ");
  if (point > 0 && !t.includes(" — ")) t = t.slice(0, point);
  return t.trim() || null;
}

/** The source window's label (read from the same-origin opener), or null. */
export function libelleSource({ win, jeton, appTitle }) {
  if (etatSource({ win, jeton }) !== "ouverte") return null;
  try {
    return libelleDepuisTitre(win.opener.document?.title, appTitle);
  } catch {
    return null;
  }
}

/** The pathname the source window currently shows, or null. */
export function cheminSource({ win, jeton }) {
  if (etatSource({ win, jeton }) !== "ouverte") return null;
  try {
    return win.opener.location?.pathname ?? null;
  } catch {
    return null;
  }
}

/**
 * « Retourner vers la fenêtre précédente »: focus the source window when it is open, then close this
 * window if it holds nothing unsaved (it was opened by script, so it may; the browser brings its
 * opener back to the front). With unsaved work here, this window stays open behind (XD-246).
 * @param {{ win: any, jeton: string | null, modifie?: boolean }} p
 * @returns {{ ok: true, fermee: boolean } | { ok: false, raison: "fermee" | "absente" }}
 */
export function retournerVersSource({ win, jeton, modifie = false }) {
  const etat = etatSource({ win, jeton });
  if (etat !== "ouverte") return { ok: false, raison: etat };
  try {
    win.opener.focus();
  } catch {
    /* a browser may refuse to raise another tab: closing this one still brings the opener back */
  }
  if (modifie) return { ok: true, fermee: false };
  try {
    win.close();
  } catch {
    return { ok: true, fermee: false };
  }
  // `closed` turns true at once when the browser accepts to close a script-opened window.
  return { ok: true, fermee: Boolean(win.closed) };
}
